<script setup lang="ts">
import 'vue-multiselect/dist/vue-multiselect.min.css'
import '@/assets/multiselect.css'
import Multiselect from 'vue-multiselect'
import { computed, defineModel, defineProps } from 'vue'

export interface Option {
  id: string | number
  value: any
  label?: string | null
}

const props = defineProps({
  options: {
    type: Array as () => Option[],
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  required: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
})

const modelValue = defineModel<any>()

const selectedOptions = computed(() => {
  if (modelValue.value === null || modelValue.value === undefined) return props.multiple ? [] : null

  if (props.multiple) {
    const values = Array.isArray(modelValue.value) ? modelValue.value : [modelValue.value]
    return props.options.filter((opt) => values.includes(opt.value))
  } else {
    return props.options.find((opt) => opt.value === modelValue.value) || null
  }
})

function handleModelUpdate(val: any) {
  if (props.multiple) {
    modelValue.value = val ? val.map((item: any) => item.value) : []
  } else {
    modelValue.value = val ? val.value : null
  }
}

const emit = defineEmits(['open', 'close'])

function onOpen() {
  emit('open')
}

function onClose() {
  emit('close')
}
</script>

<template>
  <label class="form-control w-full">
    <div class="label" v-if="label">
      <span class="label-text font-semibold">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </span>
    </div>

    <Multiselect
      :model-value="selectedOptions"
      @update:model-value="handleModelUpdate"
      @open="onOpen"
      @close="onClose"
      :options="options"
      :multiple="multiple"
      :required="required"
      :searchable="true"
      :close-on-select="true"
      :placeholder="placeholder || 'Sélectionner ' + label + '...'"
      select-label="Sélectionner"
      selected-label="Sélectionné"
      deselect-label="Retirer"
      label="label"
      track-by="value"
      :disabled="disabled"
    >
      <template #noOptions>
        <div class="p-2">Aucune option disponible</div>
      </template>
      <template #noResult>
        <div class="p-2">Aucun résultat trouvé</div>
      </template>
    </Multiselect>
  </label>
</template>

<style>
/* Ajustements pour correspondre au style DaisyUI/BS2 */
.multiselect {
  position: relative;
}
.multiselect--active {
  z-index: 9999 !important;
}
.multiselect--active .multiselect__content-wrapper {
  z-index: 9999 !important;
}
.multiselect__tags {
  border: 1px solid rgba(0, 0, 0, 0.2);
  background-color: var(--color-base-100);
  min-height: 2.5rem;
  border-radius: 0.5rem !important;
}
.multiselect__input,
.multiselect__single {
  background-color: var(--color-base-100);
  color: var(--color-base-content);
}
.multiselect__option--highlight {
  background-color: var(--color-primary) !important;
  color: var(--color-primary-content) !important;
}
.multiselect__tag {
  background-color: var(--color-primary);
  color: var(--color-primary-content);
}
.multiselect__tag-icon:after {
  color: var(--color-primary-content);
}
.multiselect__tag-icon:hover {
  background-color: var(--color-primary);
}
</style>
